
import React from 'react';
import { BookingDetails } from '../types';

interface Props {
  booking: BookingDetails;
  onReserve: () => void;
  onBackToPilot: () => void;
}

const StayLocalPage: React.FC<Props> = ({ booking, onReserve, onBackToPilot }) => {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Refined Demo Header */}
      <div className="flex items-center justify-between py-4 border-b border-gray-100 mb-8 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 bg-white sticky top-0 z-50 shadow-sm sm:shadow-none">
        <div className="flex items-center space-x-8">
          <div className="flex items-center space-x-2">
            <i className="fas fa-location-dot text-2xl staylocal-teal"></i>
            <span className="text-xl font-bold staylocal-teal hidden sm:inline tracking-tight">StayLocal</span>
          </div>
        </div>

        <div className="hidden md:flex items-center border border-gray-200 rounded-full py-2 px-4 shadow-sm hover:shadow-md transition-all cursor-pointer space-x-4">
          <button className="text-sm font-semibold px-2">Anywhere</button>
          <div className="w-px h-6 bg-gray-200"></div>
          <button className="text-sm font-semibold px-2">Any week</button>
          <div className="w-px h-6 bg-gray-200"></div>
          <button className="text-sm text-gray-500 px-2">Add guests</button>
          <div className="bg-teal-600 p-2 rounded-full text-white">
            <i className="fas fa-search text-xs"></i>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button onClick={onBackToPilot} className="text-[9px] font-bold text-gray-500 border border-gray-200 px-3 py-1.5 rounded-full uppercase tracking-widest hover:bg-black hover:text-white hover:border-black transition-all">
            Back to Pilot Info
          </button>
          <div className="flex items-center space-x-2 border border-gray-200 rounded-full p-1 pl-3 hover:shadow-md transition-all cursor-pointer">
            <i className="fas fa-bars text-gray-600"></i>
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-white overflow-hidden">
              <i className="fas fa-user text-sm"></i>
            </div>
          </div>
        </div>
      </div>


      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{booking.title}</h1>
          <div className="flex items-center space-x-2 mt-1 text-sm text-gray-600">
            <span className="flex items-center"><i className="fas fa-star staylocal-teal mr-1"></i> 4.98</span>
            <span>•</span>
            <span className="underline font-semibold">124 guest reviews</span>
            <span>•</span>
            <span className="underline font-semibold">{booking.location}</span>
          </div>
        </div>
        <div className="hidden sm:flex items-center space-x-4">
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <i className="fas fa-share-nodes mr-2"></i> Share
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <i className="far fa-heart mr-2"></i> Save
          </button>
        </div>
      </div>

      {/* Gallery Mock */}
      <div className="relative mb-12">
        <div className="grid grid-cols-4 grid-rows-2 gap-2 rounded-xl overflow-hidden h-[450px]">
          <div className="col-span-2 row-span-2">
            <img
              src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1200&q=80"
              alt="Villa Exterior"
              className="w-full h-full object-cover hover:brightness-90 transition-all cursor-pointer"
            />
          </div>
          <div className="col-span-1 row-span-1">
            <img
              src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=600&q=80"
              alt="Villa Interior"
              className="w-full h-full object-cover hover:brightness-90 transition-all cursor-pointer"
            />
          </div>
          <div className="col-span-1 row-span-1">
            <img
              src="https://images.unsplash.com/photo-1590381105924-c72589b9ef3f?auto=format&fit=crop&w=600&q=80"
              alt="Villa Bedroom"
              className="w-full h-full object-cover hover:brightness-90 transition-all cursor-pointer"
            />
          </div>
          <div className="col-span-1 row-span-1">
            <img
              src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80"
              alt="Villa Kitchen"
              className="w-full h-full object-cover hover:brightness-90 transition-all cursor-pointer"
            />
          </div>
          <div className="col-span-1 row-span-1">
            <img
              src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600&q=80"
              alt="Villa Pool"
              className="w-full h-full object-cover hover:brightness-90 transition-all cursor-pointer"
            />
          </div>
        </div>
        <button className="absolute bottom-6 right-6 bg-white border border-black px-4 py-2 rounded-lg font-semibold text-sm shadow-md hover:bg-gray-50 flex items-center space-x-2 transition-all">
          <i className="fas fa-th mr-2"></i>
          <span>Show all photos</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12">
        <div className="lg:col-span-2">
          <div className="flex justify-between items-center pb-8 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Private Estate • Managed by Alexandra</h2>
              <p className="text-gray-600">10 guests • 5 bedrooms • 7 beds • 4 baths</p>
            </div>
            <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&q=80" className="w-12 h-12 rounded-full object-cover border border-gray-100" alt="Estate Manager Alexandra" />
          </div>
          <div className="py-8 border-b border-gray-200">
            <div className="flex items-start space-x-4 mb-6">
              <i className="fas fa-door-open text-xl mt-1 text-gray-700"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Seamless check-in</h3>
                <p className="text-gray-500 text-sm">Access the property via our secure digital keypad.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4 mb-6">
              <i className="fas fa-location-dot text-xl mt-1 text-gray-700"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Premier location</h3>
                <p className="text-gray-500 text-sm">Highly rated for its exclusive beachfront access.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <i className="far fa-calendar-check text-xl mt-1 text-gray-700"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Flexible cancellation</h3>
                <p className="text-gray-500 text-sm">Enjoy the flexibility of free cancellation up to 48 hours before stay.</p>
              </div>
            </div>
          </div>
          <div className="py-8">
            <p className="text-gray-700 leading-relaxed text-lg font-light italic">
              "Experience unparalleled luxury in this stunning oceanfront glass villa.
              Perfect for large groups seeking a peaceful retreat. Every corner is designed to bring you closer to the serenity of the sea."
            </p>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 border border-gray-100 rounded-2xl p-6 shadow-2xl bg-white">
            <div className="flex justify-between items-end mb-6">
              <div>
                <span className="text-2xl font-bold text-gray-900">${booking.pricePerNight}</span>
                <span className="text-gray-500 text-sm"> / night</span>
              </div>
              <div className="text-sm font-semibold underline text-gray-600">124 ratings</div>
            </div>

            <div className="border border-gray-300 rounded-xl mb-4 overflow-hidden">
              <div className="grid grid-cols-2 border-b border-gray-300">
                <div className="p-3 border-r border-gray-300 hover:bg-gray-50 cursor-pointer">
                  <div className="text-[10px] font-bold uppercase text-gray-900">Check-in</div>
                  <div className="text-sm text-gray-500">Pick date</div>
                </div>
                <div className="p-3 hover:bg-gray-50 cursor-pointer">
                  <div className="text-[10px] font-bold uppercase text-gray-900">Checkout</div>
                  <div className="text-sm text-gray-500">Pick date</div>
                </div>
              </div>
              <div className="p-3 hover:bg-gray-50 cursor-pointer">
                <div className="text-[10px] font-bold uppercase text-gray-900">Total Guests</div>
                <div className="text-sm text-gray-500">1 guest</div>
              </div>
            </div>

            <button
              onClick={onReserve}
              className="w-full staylocal-bg-teal text-white py-4 rounded-xl font-bold hover:brightness-105 transition-all text-lg shadow-lg active:scale-[0.98]"
            >
              Book Stay
            </button>
            <p className="text-center text-xs text-gray-500 mt-4">Transparent pricing, no hidden fees</p>

            <div className="mt-6 space-y-4 pt-6">
              <div className="flex justify-between text-gray-600">
                <span className="underline">${booking.pricePerNight} x {booking.nights} nights</span>
                <span>${booking.pricePerNight * booking.nights}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span className="underline">Cleaning fee</span>
                <span>$150</span>
              </div>
              <div className="flex justify-between text-gray-600 pb-4 border-b border-gray-100">
                <span className="underline">StayLocal service fee</span>
                <span>$210</span>
              </div>
              <div className="flex justify-between font-bold text-gray-900 pt-2 text-xl">
                <span>Total</span>
                <span>${booking.total + 360}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 py-12">
        <h2 className="text-2xl font-semibold mb-8 text-gray-900 flex items-center">
          <i className="fas fa-star staylocal-teal mr-2"></i>
          4.98 • 124 guest ratings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-20 gap-y-10">
          {[
            { name: "Jessica", date: "May 2023", text: "Truly an amazing stay. The view of the ocean from the bedroom is breathtaking. Alexandra was a wonderful host!", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=100&q=80" },
            { name: "Michael", date: "April 2023", text: "The villa is even better than the photos. Spotlessly clean and the kitchen is fully equipped. Highly recommend for families.", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=100&q=80" },
            { name: "Sarah", date: "March 2023", text: "Perfect getaway. The pool area is very private and the glass walls make you feel connected to nature.", img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=100&q=80" },
            { name: "David", date: "February 2023", text: "Experience of a lifetime. The architecture is stunning and the location is quiet yet convenient.", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=100&q=80" }
          ].map((review, i) => (
            <div key={i} className="flex flex-col space-y-4">
              <div className="flex items-center space-x-4">
                <img src={review.img} className="w-12 h-12 rounded-full object-cover" alt={review.name} />
                <div>
                  <h3 className="font-semibold text-gray-900">{review.name}</h3>
                  <p className="text-gray-500 text-sm">{review.date}</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed font-light">{review.text}</p>
            </div>
          ))}
        </div>
        <button className="mt-8 border border-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all text-gray-900">
          Show all 124 ratings
        </button>
      </div>

      <div className="py-12 border-t border-gray-100 mt-12 text-center">
        <button
          onClick={onBackToPilot}
          className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-black transition-colors"
        >
          <i className="fas fa-chevron-left mr-2"></i>
          Back to Pilot Info
        </button>
      </div>
    </div>

  );
};

export default StayLocalPage;
